/**
 * react-pdf / PDF.js loads string URLs via fetch(), which requires CORS from the file host.
 * In dev (and in prod when {@link VITE_CMS_PDF_PROXY_ENABLED} is set), we rewrite matching CMS
 * URLs to {@link CMS_PDF_PROXY_PREFIX} so the app origin serves the request and Vite/nginx
 * proxies to the CMS. The host is included in the proxy path so multiple CMS origins can
 * coexist, e.g. /__cms_pdf__/lcmstaging.devinfinitylearn.in/media/file.pdf.
 */
export const CMS_PDF_PROXY_PREFIX = "/__cms_pdf__";

const defaultProxyTargets = [
  "https://ilcmsstaging.devinfinitylearn.in",
  "https://lcmstaging.devinfinitylearn.in",
];

const proxyTargetOrigins = (): Set<string> => {
  const rawTargets =
    (import.meta.env.VITE_CMS_PDF_PROXY_TARGETS as string | undefined) ||
    (import.meta.env.VITE_CMS_PDF_PROXY_TARGET as string | undefined) ||
    defaultProxyTargets.join(",");

  return new Set(
    rawTargets
      .split(",")
      .map((target) => target.trim())
      .filter(Boolean)
      .map((target) => {
        try {
          return new URL(target).origin;
        } catch {
          return "";
        }
      })
      .filter(Boolean),
  );
};

const isProxyRewriteEnabled = () => {
  if (import.meta.env.VITE_DISABLE_CMS_PDF_PROXY === "true") return false;
  return (
    import.meta.env.DEV || import.meta.env.VITE_CMS_PDF_PROXY_ENABLED === "true"
  );
};

/** Map a remote CMS PDF URL to a same-origin path for PDF.js when proxying is enabled. */
export function urlForReactPdfFetch(url: string): string {
  if (typeof window === "undefined") return url;
  if (!isProxyRewriteEnabled()) return url;

  try {
    const parsed = new URL(url, window.location.href);
    if (parsed.origin === window.location.origin) return url;

    const cmsOrigins = proxyTargetOrigins();
    if (!cmsOrigins.has(parsed.origin)) return url;

    return `${CMS_PDF_PROXY_PREFIX}/${parsed.host}${parsed.pathname}${parsed.search}`;
  } catch {
    return url;
  }
}
